import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';

import { useEffect, useState } from 'react';
import { EventModel, useEventContext } from '../../contexts/EventContext';
import Grid from '@mui/material/Grid';
import { Monad } from '../../monad/Monad';
import { PrivilegeValue, useAuthContext } from '../../contexts/AuthContext';
import { AxiosRequestConfig } from 'axios';
import IconButton from '@mui/material/IconButton';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import InfoOutlineIcon from '@mui/icons-material/InfoOutline';

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

import { styled } from '@mui/material/styles';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { DialogCloseButton } from '../shared/DialogCloseButton';
import { LoadingState } from '../shared/LoadingState';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import { User } from 'oidc-client-ts';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import { FormControl, InputAdornment, InputLabel, MenuItem, OutlinedInput, Select } from '@mui/material';
import { ApiParticipant, useHttpClientContext } from '../../contexts/HttpClientContext';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import React from 'react';

import { EventInformationModalContent } from './EventInformationTab';
import { ParticipantsAccordion, ParticipantsList } from './ParticipantListTab';
import { QrCodeScanner } from './ScannerTab';
import { useTranslation } from 'react-i18next';
import {
    ApiCheckInResponse,
    CheckInState,
    EventModalNavigationState,
    ScannerCheckInState
} from './EventModalTypes';

export const BootstrapDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialogContent-root': {
        padding: theme.spacing(2),
    },
    '& .MuiDialogActions-root': {
        padding: theme.spacing(1),
    },
}));

const shouldRenderParticipants = (event: EventModel, user: User) => {
    const isOfficial = user.requireMinimumPrivilege(PrivilegeValue.LocalActive) || user.isFestivalOfficial() || user.isDevAdmin();
    return (event.visibleParticipantList && (event.booking || !event.preBookingReq)) || ((event.checkInPossible || event.visibleParticipantList) && isOfficial);
}

const canCheckIn = (event: EventModel, user: User) => {
    if (event.allowedToCheckIn.length > 0) {
        return event.allowedToCheckIn.indexOf(user.getMembershipNumber() ?? user.getFormattedUserId()) > -1;
    }

    return event.checkInPossible && (user.requireMinimumPrivilege(PrivilegeValue.LocalActive) && user.isFestivalOfficial() || user.isDevAdmin());
}

const normalizeMembershipNumber = (num: string): string => {
    const upper = (num ?? '').toUpperCase().trim();
    return upper.startsWith('M') ? upper.substring(1) : upper;
};

const membershipNumbersMatch = (a?: string | null, b?: string | null): boolean => {
    if (!a || !b) return false;
    return normalizeMembershipNumber(a) === normalizeMembershipNumber(b);
};

const shouldAlsoCheckInPartner = (checkInResponse: ApiCheckInResponse, participants: ApiParticipant[]) => {

    const partnerBooked = checkInResponse.booking
        && checkInResponse.booking.partnerName
        && checkInResponse.booking.partnerName.length
        && checkInResponse.booking.partnerBooked === 'yes';

    const partner = participants.find(p => membershipNumbersMatch(p.membershipNumber, checkInResponse.booking?.partnerMembershipNumber));

    return partnerBooked && partner && partner.checkedIn === 'no';
}

export const EventModal = () => {

    const eventsContext = useEventContext()
    const authContext = useAuthContext();
    const httpclientContext = useHttpClientContext();
    const { t } = useTranslation();

    const [modalNavigationState, setModalNavigationState] = useState<EventModalNavigationState>(EventModalNavigationState.eventInfo);
    const [participants, setParticipants] = useState<Monad<ApiParticipant[]>>(Monad.idle());
    const [checkInState, setCheckInState] = useState<{ [membershipNumber: string]: CheckInState }>({})
    const [scannerCheckInState, setScannerCheckInState] = useState<ScannerCheckInState>({
        status: 'idle'
    });

    const [participantSearchTerm, setParticipantSearchTerm] = useState("");
    const [participantCheckInStatusFilter, setParticipantCheckInStatusFilter] = useState<"all" | "checkedIn" | "checkedOut">("all");

    const selectedEvent = eventsContext.selectedEvent;

    const fetchParticipants = async (eventId: string) => {
        return httpclientContext.authenticatedHttpClient.get<ApiParticipant[]>('participants?eventId=' + eventId);
    }

    const checkInParticipant = async (eventId: string, membershipNumber: string) => {
        const request = {
            eventId: eventId,
            membershipNumber: membershipNumber
        };
        return httpclientContext.authenticatedHttpClient.post<ApiCheckInResponse>('check-in',
            request);
    }

    const checkOutParticipant = async (eventId: string, membershipNumber: string) => {
        const request = {
            eventId: eventId,
            membershipNumber: membershipNumber
        };
        const options: AxiosRequestConfig<any> = {
            data: request
        }
        return httpclientContext.authenticatedHttpClient.delete<ApiCheckInResponse>('check-in', options);
    }


    const handleCheckIn = async (participant: ApiParticipant) => {
        console.log("Check in", participant);
        const shouldCheckIn = participant.checkedIn !== "yes";

        setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: 'loading' } }));

        (shouldCheckIn
            ? checkInParticipant(selectedEvent!.eventId, participant.membershipNumber)
            : checkOutParticipant(selectedEvent!.eventId, participant.membershipNumber)
        ).then(response => {
            console.log(response.data.checkInStatus);

            var prev = participants.getOrElse([]);
            switch (response.data.checkInStatus) {
                case 'success':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));

                    if (shouldAlsoCheckInPartner(response.data, prev)) {
                        setCheckInState(prevState => ({
                            ...prevState, [participant.membershipNumber]: {
                                status: 'success-ask-partner',
                                partnerParticipant: prev.find(x => membershipNumbersMatch(x.membershipNumber, response.data.booking?.partnerMembershipNumber))
                            }
                        }));
                    } else {
                        setCheckInState(prevState => ({ ...prevState, [participant.membershipNumber]: { status: 'success' } }));
                    }
                    break;
                case 'removed':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "no" }) : p)));
                    setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: 'removed' } }));
                    break;
                case 'alreadyCheckedIn':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: 'alreadyCheckedIn' } }));
                    break;
                default:
                    setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: 'idle' } }));
            }
        }).catch(error => {
            console.error("Error checking in", error);
            setCheckInState(prev => ({ ...prev, [participant.membershipNumber]: { status: shouldCheckIn ? "error-checkIn" : "error-checkOut" } }))
        })

    }

    const handleCheckInPartner = async (partnerParticipant: ApiParticipant, mainParticipant: ApiParticipant) => {

        setCheckInState(prev => ({ ...prev, [partnerParticipant.membershipNumber]: { status: 'loading' } }))

        setCheckInState(prev => ({
            ...prev, [partnerParticipant.membershipNumber]: {
                status: "partner-checkIn-loading"
            }
        }));

        checkInParticipant(selectedEvent!.eventId, partnerParticipant.membershipNumber).then(response => {
            console.log(response.data.checkInStatus);
            var prev = participants.getOrElse([]);

            switch (response.data.checkInStatus) {
                case 'success':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === partnerParticipant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setCheckInState(prev => ({
                        ...prev, [partnerParticipant.membershipNumber]: {
                            status: 'success',
                        }
                    }));

                    setCheckInState(prev => ({
                        ...prev, [mainParticipant.membershipNumber]: {
                            status: 'partner-success',
                            partnerParticipant: partnerParticipant
                        }
                    }));
                    break;
                case 'alreadyCheckedIn':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === partnerParticipant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setCheckInState(prev => ({
                        ...prev, [mainParticipant.membershipNumber]: {
                            status: 'partner-alreadyCheckedIn',
                        }
                    }));
                    break;
                default:
                    setCheckInState(prev => ({ ...prev, [partnerParticipant.membershipNumber]: { status: 'idle' } }));
            }
        }).catch(error => {
            console.error("Error checking in partner", error);
            setCheckInState(prev => ({
                ...prev, [mainParticipant.membershipNumber]: {
                    status: 'partner-error',
                    partnerParticipant: partnerParticipant
                }
            }));
        });
    }

    const handleScannerCheckIn = (scannedText: string, checkOut: boolean): Promise<void> => {

        console.log("handleScannerCheckIn", scannedText);

        // eslint-disable-next-line no-control-regex
        const cleanedText = scannedText.trim().replace(/[\x00-\x1F\x7F-\x9F\u200B-\u200D\uFEFF]/g, '').toUpperCase();

        if (!/^[A-Z0-9]+$/.test(cleanedText)) {
            setScannerCheckInState({ status: "invalidFormat" });
            return Promise.resolve();
        }

        const participant = participants.getOrElse([]).find(p => {
            const memberNo = p.membershipNumber.toUpperCase();
            if (cleanedText.startsWith("M")) {
                return memberNo === cleanedText || memberNo === cleanedText.substring(1);
            } else if (cleanedText.startsWith("U")) {
                return memberNo === cleanedText;
            } else {
                return memberNo === cleanedText;
            }
        });

        if (!participant) {
            setScannerCheckInState({ status: "notBooked" });
            return Promise.resolve();
        }

        setScannerCheckInState({ status: "loading" });

        return (checkOut
            ? checkOutParticipant(selectedEvent!.eventId, participant.membershipNumber)
            : checkInParticipant(selectedEvent!.eventId, participant.membershipNumber)).then(response => {
                var prev = participants.getOrElse([]);
                switch (response.data.checkInStatus) {
                    case 'success':
                        setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));

                        if (shouldAlsoCheckInPartner(response.data, prev)) {
                            setScannerCheckInState({
                                status: 'success-ask-partner',
                                booking: response.data.booking,
                                partnerParticipant: prev.find(x => membershipNumbersMatch(x.membershipNumber, response.data.booking?.partnerMembershipNumber))
                            });
                        } else {
                            setScannerCheckInState({
                                status: "success",
                                booking: response.data.booking
                            });
                        }
                        break;
                    case 'alreadyCheckedIn':
                        setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                        setScannerCheckInState({
                            status: "alreadyCheckedIn",
                            booking: response.data.booking
                        });
                        break;
                    case 'notBooked':
                        setScannerCheckInState({ status: "notBooked" });
                        break;
                    case 'removed':
                        setParticipants(Monad.success(prev.map(p => p.membershipNumber === participant.membershipNumber ? ({ ...p, checkedIn: "no" }) : p)));
                        setScannerCheckInState({
                            status: "removed",
                            booking: response.data.booking
                        });
                        break;
                    default:
                        setScannerCheckInState({ status: "idle" });
                }
            }).catch(error => {
                console.error("Error checking in participant with scanner", error);
                setScannerCheckInState({ status: "error" });
            });
    }

    const handleScannerCheckInPartner = async (partnerParticipant: ApiParticipant) => {

        setScannerCheckInState(prev => ({ ...prev, status: "partner-checkIn-loading" }))

        checkInParticipant(selectedEvent!.eventId, partnerParticipant.membershipNumber).then(response => {
            console.log(response.data.checkInStatus);
            var prev = participants.getOrElse([]);

            switch (response.data.checkInStatus) {
                case 'success':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === partnerParticipant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));

                    setScannerCheckInState(prev => ({
                        ...prev,
                        status: 'partner-success'
                    }));
                    break;
                case 'alreadyCheckedIn':
                    setParticipants(Monad.success(prev.map(p => p.membershipNumber === partnerParticipant.membershipNumber ? ({ ...p, checkedIn: "yes" }) : p)));
                    setScannerCheckInState(prev => ({
                        ...prev,
                        status: 'partner-alreadyCheckedIn',
                    }));
                    break;
                default:
                    setScannerCheckInState(prev => ({ ...prev, status: 'idle' }));
            }
        }).catch(error => {
            console.error("Error checking in partner", error);
            setScannerCheckInState(prev => ({
                ...prev,
                status: 'partner-error',
                partnerParticipant: partnerParticipant
            }));
        });
    }

    useEffect(() => {
        if (!eventsContext.eventModalOpen) {
            setModalNavigationState(EventModalNavigationState.eventInfo);
        }

        if (!selectedEvent) {
            return
        };
        authContext.user.match({
            loggedIn: async (user) => {
                if (shouldRenderParticipants(selectedEvent, user)) {
                    setParticipants(Monad.loading());
                    fetchParticipants(selectedEvent.eventId)
                        .then((participantsResponse) => {
                            setParticipants(Monad.success(participantsResponse.data));
                        })
                        .catch((error) => {
                            console.error(error);
                            setParticipants(Monad.error("Kunde inte hämta deltagare."));
                        });
                }
            }
        });
        return () => {
            setCheckInState({});
            setParticipantSearchTerm("");
            setParticipantCheckInStatusFilter("all");
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventsContext.eventModalOpen]);


    return (
        <BootstrapDialog
            onClose={eventsContext!.closeEventModal}
            aria-labelledby="customized-dialog-title"
            open={eventsContext!.eventModalOpen}
        >
            <DialogTitle sx={{ m: 0, p: 2, pr: 7 }} id="customized-dialog-title">
                {modalNavigationState === EventModalNavigationState.eventInfo && selectedEvent?.heading}
                {modalNavigationState === EventModalNavigationState.participantList && "Incheckning"}
                {modalNavigationState === EventModalNavigationState.scanner && "Incheckning QR-kod"}
            </DialogTitle>
            <DialogCloseButton onClick={eventsContext!.closeEventModal} />
            {modalNavigationState === EventModalNavigationState.participantList && authContext.user.match({
                loggedIn(user) {
                    const displayCheckinStatusSelect = selectedEvent && canCheckIn(selectedEvent, user);
                    return (<Box sx={{ px: 1.5, pb: 1.5 }}>
                        <Grid container spacing={1}>
                            <Grid size={displayCheckinStatusSelect ? 8 : 12}>
                                <FormControl fullWidth variant="outlined">
                                    <InputLabel htmlFor="outlined-adornment-search-participant">{t("participants.search")}</InputLabel>
                                    <OutlinedInput
                                        id="outlined-adornment-search-participant"
                                        type="text"
                                        value={participantSearchTerm}
                                        onChange={evt => setParticipantSearchTerm(evt.target.value)}
                                        endAdornment={
                                            <InputAdornment position="end">
                                                <IconButton edge="end" onClick={() => setParticipantSearchTerm("")}>
                                                    <HighlightOffIcon />
                                                </IconButton>
                                            </InputAdornment>
                                        }
                                        label={t("participants.search")}
                                    />
                                </FormControl>
                            </Grid>
                            {displayCheckinStatusSelect &&
                                <Grid size={4}>
                                    <FormControl fullWidth>
                                        <InputLabel id="checkIn-status-select-label">{t("scanner.status_title")}</InputLabel>
                                        <Select
                                            labelId="checkIn-status-select-label"
                                            id="checkIn-status-select"
                                            label={t("scanner.status_title")}
                                            value={participantCheckInStatusFilter}
                                            onChange={(evt) => setParticipantCheckInStatusFilter(evt.target.value as "all" | "checkedIn" | "checkedOut")}
                                        >
                                            <MenuItem value={"all"}>{t("participants.filter_all")}</MenuItem>
                                            <MenuItem value={"checkedIn"}>{t("participants.filter_checked_in")}</MenuItem>
                                            <MenuItem value={"checkedOut"}>{t("participants.filter_checked_out")}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            }
                        </Grid>

                        {displayCheckinStatusSelect && participants.match({
                            success: (data) => <Typography component='p' mt={1}>Antal incheckade: {data.filter(p => p.checkedIn === "yes").length} av {data.length}</Typography>

                        })}

                    </Box>)
                }
            })

            }
            <DialogContent dividers>
                {modalNavigationState === EventModalNavigationState.eventInfo &&
                    <EventInformationModalContent changeModalState={(state: EventModalNavigationState) => setModalNavigationState(state)} />
                }
                {modalNavigationState === EventModalNavigationState.participantList &&
                    <>
                        {authContext.user.match({
                            loggedIn(user) {
                                return participants.match({
                                    success(data) {
                                        const filter = () => {
                                            var filtered = data.filter(p => participantSearchTerm.length ? p.name.toLocaleLowerCase().indexOf(participantSearchTerm.toLocaleLowerCase()) > -1 : p)

                                            switch (participantCheckInStatusFilter) {
                                                case 'all':
                                                    return filtered;
                                                case 'checkedIn':
                                                    return filtered.filter(p => p.checkedIn === "yes");
                                                case 'checkedOut':
                                                    return filtered.filter(p => p.checkedIn !== "yes");
                                            }
                                        }

                                        const filteredParticipants = filter();
                                        return <>
                                            {selectedEvent && !canCheckIn(selectedEvent, user) &&
                                                <ParticipantsList participants={filteredParticipants} />
                                            }
                                            {selectedEvent && canCheckIn(selectedEvent, user) &&
                                                <ParticipantsAccordion
                                                    participants={filteredParticipants}
                                                    onCheckIn={handleCheckIn}
                                                    onCheckInPartner={handleCheckInPartner}
                                                    checkInState={checkInState} />
                                            }
                                        </>
                                    },
                                    loading: () => (
                                        <LoadingState label={t("event_modal.loading_participants")} height="auto" />
                                    ),
                                    idle: () => (
                                        <Alert severity='info'>Inga deltagare att visa</Alert>
                                    ),
                                    error: () => (
                                        <Alert severity='error'>Kunde inte hämta deltagare</Alert>
                                    )
                                })
                            }
                        })}
                    </>
                }
                {modalNavigationState === EventModalNavigationState.scanner &&
                    <QrCodeScanner
                        handleScannerCheckIn={handleScannerCheckIn}
                        handleScannerCheckInPartner={handleScannerCheckInPartner}
                        scannerCheckInState={scannerCheckInState}
                    />
                }


            </DialogContent>
            {selectedEvent && authContext.user.match({
                loggedIn: (user) =>

                    shouldRenderParticipants(selectedEvent, user) &&
                    <DialogActions style={{ justifyContent: 'center' }}>
                        <Tabs value={modalNavigationState} onChange={(evt, newValue: EventModalNavigationState) => { setModalNavigationState(newValue); }} centered>
                            <Tab icon={<InfoOutlineIcon />} label={t("event_modal.information")} value={EventModalNavigationState.eventInfo} />
                            <Tab icon={<GroupsOutlinedIcon />} label={participants.match({
                                loading: () => (<span style={{ display: 'flex', alignItems: 'center' }}>{t("event_modal.participants")} <CircularProgress sx={{ ml: 0.625 }} size={"1rem"} /></span>),
                                success: (data) => (<>{`${t("event_modal.participants")} (${data.length})`}</>)
                            })} value={EventModalNavigationState.participantList} />
                            {canCheckIn(selectedEvent, user) &&
                                <Tab icon={<QrCodeScannerIcon />} label={t("event_modal.scanner")} value={EventModalNavigationState.scanner} />
                            }
                        </Tabs>
                    </DialogActions>
            })}
        </BootstrapDialog>
    );
};
